/**
 * Weighted profile completion score (0–100).
 * See docs/profile-completion-weights.md for rationale.
 */

export const PROFILE_COMPLETION_WEIGHTS = {
  basicProfile: 20,
  prompts: 35,
  portfolio: 17,
  profilePhoto: 7,
  resume: 7,
  languages: 4,
  references: 3,
  professionalLinks: 3,
  workCulture: 4,
} as const;

/** Full screening-readiness score expects this many prompts (video + description each). */
export const TARGET_SCREENING_PROMPT_COUNT = 6;

export type ProfileCompletionSection = keyof typeof PROFILE_COMPLETION_WEIGHTS;

export const PROFILE_COMPLETION_SECTION_LABELS: Record<ProfileCompletionSection, string> = {
  basicProfile: 'Profile basics (name, contact, location)',
  prompts: 'Screening prompts (video and description)',
  portfolio: 'Portfolio content',
  profilePhoto: 'Profile photo',
  resume: 'Resume',
  languages: 'Languages',
  references: 'References',
  professionalLinks: 'Professional links',
  workCulture: 'Ideal work culture',
};

export interface ProfileCompletionBreakdownItem {
  section: ProfileCompletionSection;
  label: string;
  weight: number;
  earned: number;
  /** 0–100 completion within this section */
  sectionPercent: number;
}

export interface ProfileCompletionResult {
  percent: number;
  breakdown: ProfileCompletionBreakdownItem[];
  suggestions: string[];
  isFullyComplete: boolean;
}

type ScoringPrompt = {
  fileUrl: string;
  supportedDocumentUrl: string;
  description: string;
};

type ScoringPortfolio = {
  overview?: string;
  projects?: unknown[];
  references?: unknown[];
  languages?: unknown[];
  workCulture?: unknown[];
  accomplishments?: unknown[];
  certifications?: unknown[];
  organizations?: unknown[];
};

function asRecord(value: unknown): Record<string, unknown> {
  return value && typeof value === 'object' ? (value as Record<string, unknown>) : {};
}

function hasText(value: unknown): boolean {
  return Boolean(String(value ?? '').trim());
}

function hasItems(value: unknown): boolean {
  return Array.isArray(value) && value.length > 0;
}

function normalizePromptForScoring(raw: unknown): ScoringPrompt {
  const p = asRecord(raw);
  return {
    fileUrl: String(p.fileUrl ?? p.file_url ?? '').trim(),
    supportedDocumentUrl: String(p.supportedDocumentUrl ?? p.supported_document_url ?? '').trim(),
    description: String(p.description ?? '').trim(),
  };
}

/** Video + description only; supporting documents are optional extras. */
export function promptCompletenessPoints(prompt: ScoringPrompt): number {
  let points = 0;
  if (prompt.fileUrl && prompt.fileUrl !== 'N/A') points += 1;
  if (prompt.description) points += 1;
  return points;
}

function scorePromptSection(promptsRaw: unknown): number {
  const list = Array.isArray(promptsRaw) ? promptsRaw : [];
  if (list.length === 0) return 0;

  const earned = list.reduce(
    (sum, item) => sum + promptCompletenessPoints(normalizePromptForScoring(item)),
    0
  );
  const denominator = TARGET_SCREENING_PROMPT_COUNT * 2;
  const ratio = Math.min(1, earned / denominator);
  return ratio * PROFILE_COMPLETION_WEIGHTS.prompts;
}

export function countScreeningPrompts(promptsRaw: unknown): number {
  return Array.isArray(promptsRaw) ? promptsRaw.length : 0;
}

export function screeningPromptProgress(promptsRaw: unknown): {
  count: number;
  target: number;
  slotsRemaining: number;
} {
  const count = countScreeningPrompts(promptsRaw);
  const target = TARGET_SCREENING_PROMPT_COUNT;
  return {
    count,
    target,
    slotsRemaining: Math.max(0, target - count),
  };
}

function normalizePortfolio(raw: unknown): ScoringPortfolio {
  const p = asRecord(raw);
  return {
    overview: String(p.overview ?? ''),
    projects: Array.isArray(p.projects) ? p.projects : [],
    references: Array.isArray(p.references) ? p.references : [],
    languages: Array.isArray(p.languages) ? p.languages : [],
    workCulture: Array.isArray(p.workCulture) ? p.workCulture : [],
    accomplishments: Array.isArray(p.accomplishments) ? p.accomplishments : [],
    certifications: Array.isArray(p.certifications) ? p.certifications : [],
    organizations: Array.isArray(p.organizations) ? p.organizations : [],
  };
}

/** Portfolio subsection weights (sum to PROFILE_COMPLETION_WEIGHTS.portfolio). */
const PORTFOLIO_SUBWEIGHTS = {
  overview: 5,
  projects: 6,
  references: 3,
  languages: 2,
  workCulture: 2,
  accomplishments: 1,
  certifications: 1,
} as const;

function scorePortfolioSection(portfolioRaw: unknown): number {
  const portfolio = normalizePortfolio(portfolioRaw);
  let earned = 0;

  if (hasText(portfolio.overview)) earned += PORTFOLIO_SUBWEIGHTS.overview;
  if (hasItems(portfolio.projects)) earned += PORTFOLIO_SUBWEIGHTS.projects;
  if (hasItems(portfolio.references)) earned += PORTFOLIO_SUBWEIGHTS.references;
  if (hasItems(portfolio.languages)) earned += PORTFOLIO_SUBWEIGHTS.languages;
  if (hasItems(portfolio.workCulture)) earned += PORTFOLIO_SUBWEIGHTS.workCulture;
  if (hasItems(portfolio.accomplishments)) {
    earned += PORTFOLIO_SUBWEIGHTS.accomplishments;
  }
  if (hasItems(portfolio.certifications)) {
    earned += PORTFOLIO_SUBWEIGHTS.certifications;
  }

  return Math.min(earned, PROFILE_COMPLETION_WEIGHTS.portfolio);
}

function hasProfilePhoto(user: Record<string, unknown>): boolean {
  return Boolean(user.profileImg || user.profileImage || user.avatarUrl || user.photoUrl);
}

function hasResume(user: Record<string, unknown>): boolean {
  return hasText(user.resume);
}

function scoreLanguagesSection(portfolioRaw: unknown): number {
  const portfolio = normalizePortfolio(portfolioRaw);
  return hasItems(portfolio.languages) ? PROFILE_COMPLETION_WEIGHTS.languages : 0;
}

function scoreReferencesSection(portfolioRaw: unknown): number {
  const portfolio = normalizePortfolio(portfolioRaw);
  return hasItems(portfolio.references) ? PROFILE_COMPLETION_WEIGHTS.references : 0;
}

function scoreWorkCultureSection(portfolioRaw: unknown): number {
  const portfolio = normalizePortfolio(portfolioRaw);
  return hasItems(portfolio.workCulture) ? PROFILE_COMPLETION_WEIGHTS.workCulture : 0;
}

function scoreProfessionalLinks(user: Record<string, unknown>): number {
  let earned = 0;
  if (hasText(user.linkedin)) earned += 2;
  if (hasText(user.otherSocial) || hasText(user.portfolioUrl)) earned += 1;
  return Math.min(earned, PROFILE_COMPLETION_WEIGHTS.professionalLinks);
}

/** Onboarding fields: name, demographics, contact, and location. */
function scoreBasicProfileSection(user: Record<string, unknown>): number {
  const firstName = String(user.firstName ?? user.first_name ?? user.fname ?? '').trim();
  const lastName = String(user.lastName ?? user.last_name ?? user.lname ?? '').trim();
  const gender = String(user.gender ?? '').trim();
  const dateOfBirth = String(user.dateOfBirth ?? user.date_of_birth ?? '').trim();
  const country = String(user.country ?? '').trim();
  const state = String(user.state ?? '').trim();
  const city = String(user.city ?? '').trim();
  const phoneNumber = String(user.phoneNumber ?? user.phone_number ?? '').trim();
  const username = String(user.username ?? '').trim();

  let earned = 0;
  if (firstName) earned += 2.5;
  if (lastName) earned += 2.5;
  if (gender) earned += 2;
  if (dateOfBirth) earned += 2;
  if (country) earned += 2;
  if (state) earned += 1.5;
  if (city) earned += 1.5;
  if (phoneNumber) earned += 3;
  if (username) earned += 3;

  return Math.min(earned, PROFILE_COMPLETION_WEIGHTS.basicProfile);
}

function buildBreakdownItem(
  section: ProfileCompletionSection,
  earned: number
): ProfileCompletionBreakdownItem {
  const weight = PROFILE_COMPLETION_WEIGHTS[section];
  return {
    section,
    label: PROFILE_COMPLETION_SECTION_LABELS[section],
    weight,
    earned: Math.round(earned * 10) / 10,
    sectionPercent: weight > 0 ? Math.round((earned / weight) * 1000) / 10 : 0,
  };
}

const SUGGESTION_COPY: Record<ProfileCompletionSection, string> = {
  basicProfile: 'Finish your profile basics (name, phone, and location)',
  prompts: 'Add more screening prompts with video and description (6 recommended for 100%)',
  portfolio: 'Add portfolio projects or an overview',
  profilePhoto: 'Add a profile photo',
  resume: 'Upload your resume',
  languages: 'Add languages you speak',
  references: 'Add professional references',
  professionalLinks: 'Add LinkedIn or other professional links',
  workCulture: 'Describe your ideal work culture',
};

function buildSuggestions(breakdown: ProfileCompletionBreakdownItem[]): string[] {
  return breakdown
    .filter((item) => item.earned < item.weight)
    .sort((a, b) => b.weight - b.earned - (a.weight - a.earned))
    .map((item) => SUGGESTION_COPY[item.section]);
}

export function draftPromptsForScoring(
  drafts: Array<{
    hasVideo: boolean;
    hasDescription: boolean;
    hasDocument?: boolean;
  }>
) {
  return drafts.map((draft) => ({
    fileUrl: draft.hasVideo ? 'draft://video' : '',
    supportedDocumentUrl: '',
    description: draft.hasDescription ? 'draft' : '',
  }));
}

export function computeProfileCompletionScore(
  userRaw: unknown,
  promptsRaw: unknown,
  portfolioRaw: unknown
): ProfileCompletionResult {
  const user = asRecord(userRaw);
  const basicProfileEarned = scoreBasicProfileSection(user);
  const promptsEarned = scorePromptSection(promptsRaw);
  const portfolioEarned = scorePortfolioSection(portfolioRaw);
  const photoEarned = hasProfilePhoto(user) ? PROFILE_COMPLETION_WEIGHTS.profilePhoto : 0;
  const resumeEarned = hasResume(user) ? PROFILE_COMPLETION_WEIGHTS.resume : 0;
  const languagesEarned = scoreLanguagesSection(portfolioRaw);
  const referencesEarned = scoreReferencesSection(portfolioRaw);
  const linksEarned = scoreProfessionalLinks(user);
  const workCultureEarned = scoreWorkCultureSection(portfolioRaw);

  const breakdown = [
    buildBreakdownItem('basicProfile', basicProfileEarned),
    buildBreakdownItem('prompts', promptsEarned),
    buildBreakdownItem('portfolio', portfolioEarned),
    buildBreakdownItem('profilePhoto', photoEarned),
    buildBreakdownItem('resume', resumeEarned),
    buildBreakdownItem('languages', languagesEarned),
    buildBreakdownItem('references', referencesEarned),
    buildBreakdownItem('professionalLinks', linksEarned),
    buildBreakdownItem('workCulture', workCultureEarned),
  ];

  const rawTotal = breakdown.reduce((sum, item) => sum + item.earned, 0);
  const percent = Math.max(0, Math.min(100, Math.round(rawTotal)));
  const suggestions = buildSuggestions(breakdown);

  return {
    percent,
    breakdown,
    suggestions,
    isFullyComplete: percent >= 100,
  };
}

/** Backend field `profile_completion_percentage` (camelCase in v1 API). */
export function readProfileCompletionPercentFromUser(userRaw: unknown): number | null {
  const user = asRecord(userRaw);
  const backendPercent = Number(
    user.profileCompletionPercentage ?? user.profile_completion_percentage
  );
  if (!Number.isFinite(backendPercent) || backendPercent < 0) {
    return null;
  }
  return Math.max(0, Math.min(100, Math.round(backendPercent)));
}

/**
 * Screening readiness for candidate UI. Percent comes from the user record
 * (`profile_completion_percentage`); section breakdown stays client-computed
 * for detail views until the API exposes it.
 */
export function resolveScreeningReadiness(
  userRaw: unknown,
  promptsRaw: unknown,
  portfolioRaw: unknown
): ProfileCompletionResult {
  const computed = computeProfileCompletionScore(userRaw, promptsRaw, portfolioRaw);
  const backendPercent = readProfileCompletionPercentFromUser(userRaw);
  if (backendPercent == null) {
    return computed;
  }
  return {
    ...computed,
    percent: backendPercent,
    isFullyComplete: backendPercent >= 100,
  };
}

export function resolveScreeningReadinessPercent(
  userRaw: unknown,
  promptsRaw: unknown,
  portfolioRaw: unknown
): number {
  return resolveScreeningReadiness(userRaw, promptsRaw, portfolioRaw).percent;
}

export function countCompletedProfileSections(
  breakdown: Array<{ earned: number; weight: number }>
): { completed: number; total: number } {
  return {
    completed: breakdown.filter((section) => section.earned >= section.weight - 0.05).length,
    total: breakdown.length,
  };
}

export function profileCompletionMessage(result: ProfileCompletionResult): string {
  const { percent, suggestions, isFullyComplete } = result;

  if (isFullyComplete) {
    return '🎉 Congratulations! You’ve completed your SkipScreen profile.';
  }

  if (percent >= 85) {
    const next = suggestions[0] ?? 'finish the remaining profile sections';
    return `Almost there! ${next} to reach 100%.`;
  }

  if (percent >= 60) {
    const next = suggestions.slice(0, 2).join('; ') || 'keep building your profile';
    return `Strong progress. Focus next on: ${next}.`;
  }

  if (percent >= 35) {
    const next = suggestions[0] ?? 'complete your screening prompts';
    return `You’re on your way. Priority next step: ${next}.`;
  }

  if (percent >= 15) {
    return 'Good start! Complete screening prompts and portfolio sections to stand out to recruiters.';
  }

  if (percent > 0) {
    return 'Profile basics saved — keep going with your screening prompts.';
  }

  return 'Welcome! Start with your screening prompts';
}
